import { html } from '../lib/html.js';
import { onlyDigits } from '../lib/masks.js';
import { busy, confirm, toastError } from '../lib/ui.js';
import { OWNER_ANSWERS, OWNER_ICON, UPCOMING_STATUS, addMonths, fmtDay, fmtKm, fmtMonth, maintenances, meterShort, meterUnit, nextText, setMeterUnit, todayBR } from '../services/maintenances.js';

const hours = () => meterUnit() === 'HOURS';
import { vehicles } from '../services/vehicles.js';
import { pill } from './maintenance-ui.js';
import { Page, go } from './base.js';

/**
 * /vehicles/:vehicleId/lembretes: o dono informa a km atual e a última troca dos itens liberados no admin
 * para ativar os lembretes. Uma pergunta por tela, respostas em botões (tocar já avança) e "Não lembro"
 * sempre vale; só a km é obrigatória. Também serve para corrigir (30 dias, antes de uma oficina registrar).
 *
 * Passos: intro → km → uma tela por item (com "Sei a data exata") → salvar → pronto.
 */
class InitialDataPage extends Page {
  template() {
    return html`
      <ion-header class="ion-no-border">
        <ion-toolbar>
          <ion-buttons slot="start"><ion-button class="wiz-back" aria-label="Voltar"><ion-icon slot="icon-only" name="chevron-back"></ion-icon></ion-button></ion-buttons>
          <ion-title class="wiz-step"></ion-title>
          <ion-buttons slot="end"><ion-button class="wiz-skip" hidden>Pular</ion-button></ion-buttons>
        </ion-toolbar>
        <div class="wiz-progress" hidden><i></i></div>
      </ion-header>
      <ion-content class="ion-padding">
        <div class="narrow body wiz"></div>
      </ion-content>`;
  }

  get vid() {
    if (this.vehicleId) return String(this.vehicleId);
    return decodeURIComponent(location.pathname.match(/\/vehicles\/([^/?#]+)/)?.[1] ?? '');
  }

  setup() {
    this.$('.wiz-back').addEventListener('click', () => this.back());
    this.$('.wiz-skip').addEventListener('click', () => this.next());
    this.$('.body').addEventListener('click', (e) => {
      const el = e.target.closest('[data-action]');
      if (el) this.action(el.dataset.action, el);
    });
  }

  async onEnter() {
    this.step = 'loading';
    this.paint();
    try {
      const [d, v] = await Promise.all([maintenances.initialGet(this.vid), vehicles.get(this.vid)]);
      setMeterUnit(d.meterUnit ?? v.vehicle.meterUnit);
      this.questions = d.questions;
      this.kmQuestion = d.kmQuestion ?? 'Quantos km o {carro} tem hoje?';
      this.vehicle = v.vehicle;
      this.label = v.access?.nickname || [v.vehicle.brand, v.vehicle.model].filter(Boolean).join(' ') || v.vehicle.plateFormatted || (v.vehicle.serialNumber ? `Série ${v.vehicle.serialNumber}` : 'veículo');
      this.existing = d.initialData;
      if (!d.canCreate && !d.initialData?.canCorrect) {
        this.step = 'closed';
        this.closedReason = d.initialData ? 'done' : 'owner';
      } else {
        // correção: começa com as respostas anteriores
        const prev = d.initialData;
        this.km = prev ? String(prev.km) : '';
        this.answers = Object.fromEntries((prev?.items ?? []).map((it) => [it.typeKey, { answer: it.answer, month: it.answer === 'EXACT' ? it.lastDate.slice(0, 7) : '', lastKm: it.lastKm !== null ? String(it.lastKm) : '' }]));
        this.step = 'intro';
      }
    } catch (err) {
      this.step = 'error';
      this.error = err.status === 404 ? 'Veículo não encontrado.' : err.message;
    }
    this.paint();
  }

  /** Ordem dos passos: intro, km, cada pergunta (i), e a data exata como subpasso da pergunta atual. */
  get total() {
    return 1 + this.questions.length;
  }

  back() {
    if (this.step === 'exact') return this.show(this.qi);
    if (typeof this.step === 'number') return this.step === 0 ? this.show('km') : this.show(this.step - 1);
    if (this.step === 'km') return this.show('intro');
    return go(`/vehicles/${this.vid}`, 'back');
  }

  /** Pula a pergunta atual (sem resposta) e segue. */
  next() {
    if (typeof this.step === 'number' || this.step === 'exact') {
      delete this.answers[this.questions[this.qi].key];
      return this.advance();
    }
  }

  advance() {
    const i = (this.qi ?? -1) + 1;
    return i < this.questions.length ? this.show(i) : this.save();
  }

  show(step) {
    this.step = step;
    if (typeof step === 'number') this.qi = step;
    if (step === 'km') this.qi = -1;
    this.paint();
    this.$('ion-content').scrollToTop?.(0);
  }

  async action(name, el) {
    if (name === 'start') return this.show('km');
    if (name === 'vehicle') return go(`/vehicles/${this.vid}`, 'back');
    if (name === 'km-next') return this.kmNext();
    if (name === 'answer') {
      this.answers[this.questions[this.qi].key] = { answer: el.dataset.answer };
      return this.advance();
    }
    if (name === 'exact') return this.show('exact');
    if (name === 'exact-next') return this.exactNext();
    if (name === 'quick') return this.show(this.qi);
    if (name === 'redo') return this.onEnter();
  }

  async kmNext() {
    const input = this.$('.km-input');
    this.km = onlyDigits(input.value);
    if (!this.km) {
      this.$('.km-error').textContent = hours() ? 'Informe as horas do horímetro.' : 'Informe a km do painel.';
      return;
    }
    // só pergunta quando o número parece fora do normal para a idade do veículo (zero a mais)
    const age = this.vehicle.modelYear ? Math.max(1, new Date().getFullYear() - this.vehicle.modelYear + 1) : 12;
    if (!hours() && Number(this.km) > Math.max(age * 40000, 80000)) {
      const ok = await confirm({ header: `${fmtKm(this.km)}, confere?`, message: 'Parece bastante para a idade do veículo. Confira o número no painel.', confirmText: 'Está certo', cancelText: 'Corrigir' });
      if (!ok) return input.setFocus?.();
    }
    return this.advance();
  }

  exactNext() {
    const q = this.questions[this.qi];
    const month = String(this.$('.month-input').value ?? '').slice(0, 7);
    const lastKm = onlyDigits(this.$('.lastkm-input').value);
    const err = this.$('.exact-error');
    if (!month) return (err.textContent = 'Informe o mês e o ano da troca.');
    if (month > todayBR().slice(0, 7)) return (err.textContent = 'O mês não pode ser no futuro.');
    if (lastKm && Number(lastKm) > Number(this.km)) return (err.textContent = `${hours() ? 'O horímetro da troca não pode ser maior que o atual' : 'A km da troca não pode ser maior que a atual'} (${fmtKm(this.km)}).`);
    this.answers[q.key] = { answer: 'EXACT', month, lastKm };
    return this.advance();
  }

  /** Prévia da próxima troca na data exata (mesmo cálculo da API). */
  preview() {
    const q = this.questions[this.qi];
    const month = String(this.$('.month-input')?.value ?? '').slice(0, 7);
    const lastKm = onlyDigits(this.$('.lastkm-input')?.value);
    const box = this.$('.next-box');
    if (!box) return;
    if (!month) return (box.hidden = true);
    const today = todayBR();
    const lastDate = [`${month}-15`, today].sort()[0];
    const interval = q.intervalMeter ?? (hours() ? q.intervalHours : q.intervalKm);
    const nextKm = lastKm && interval ? Number(lastKm) + interval : null;
    const nextDate = addMonths(lastDate, q.intervalMonths);
    const left = nextKm !== null ? nextKm - Number(this.km) : null;
    box.hidden = false;
    box.querySelector('strong').textContent = `${nextKm !== null ? `Em ${fmtKm(nextKm)} ou ` : ''}${fmtMonth(nextDate)}`;
    box.querySelector('span:last-child').textContent =
      nextDate < today || (left !== null && left <= 0) ? 'Já passou do prazo: vamos avisar você.' : left !== null ? `Faltam ${fmtKm(left)} · o que vier primeiro` : hours() ? 'Com o horímetro da troca, avisamos também pelas horas de uso.' : 'Com a km da troca, avisamos também pela quilometragem.';
  }

  async save() {
    this.step = 'saving';
    this.paint();
    const items = this.questions
      .filter((q) => this.answers[q.key])
      .map((q) => {
        const a = this.answers[q.key];
        return { typeKey: q.key, answer: a.answer, ...(a.answer === 'EXACT' ? { month: a.month, ...(a.lastKm ? { lastKm: Number(a.lastKm) } : {}) } : {}) };
      });
    const body = { km: Number(this.km), items };
    try {
      this.result = this.existing ? await maintenances.initialCorrect(this.vid, { ...body, version: this.existing.version }) : await maintenances.initialCreate(this.vid, body);
      this.show('done');
    } catch (err) {
      toastError(err.message);
      this.show(this.questions.length ? this.questions.length - 1 : 'km');
    }
  }

  /** Cabeçalho: voltar/fechar, "Passo x de y", Pular e a barra de progresso. */
  paintHeader() {
    const s = this.step;
    const inQuestion = typeof s === 'number' || s === 'exact';
    const n = s === 'km' ? 1 : inQuestion ? this.qi + 2 : 0;
    const back = this.$('.wiz-back');
    back.hidden = s === 'done' || s === 'saving' || s === 'loading';
    back.querySelector('ion-icon').name = s === 'intro' || s === 'closed' || s === 'error' ? 'close' : 'chevron-back';
    back.setAttribute('aria-label', s === 'intro' ? 'Fechar' : 'Voltar');
    this.$('.wiz-step').textContent = n ? `Passo ${n} de ${this.total}` : '';
    this.$('.wiz-skip').hidden = !inQuestion;
    const bar = this.$('.wiz-progress');
    bar.hidden = !n;
    if (n) bar.querySelector('i').style.width = `${Math.round((n / this.total) * 100)}%`;
  }

  paint() {
    this.paintHeader();
    const body = this.$('.body');
    const s = this.step;
    if (s === 'loading' || s === 'saving') {
      body.innerHTML = '<div class="center"><ion-spinner></ion-spinner></div>';
      return;
    }
    if (s === 'error') {
      body.innerHTML = html`<div class="center"><ion-icon name="alert-circle-outline" class="empty-icon"></ion-icon><p>${this.error}</p><ion-button fill="outline" data-action="vehicle">Voltar</ion-button></div>`.value;
      return;
    }
    if (s === 'closed') {
      body.innerHTML = html`
        <div class="panel empty-panel">
          <div class="empty-badge"><ion-icon name="checkmark-circle-outline"></ion-icon></div>
          <strong>${this.closedReason === 'done' ? 'Dados iniciais já informados' : 'Só o dono informa os dados iniciais'}</strong>
          <p>${this.closedReason === 'done' ? 'O prazo para corrigir terminou ou uma empresa já registrou serviço. Os próximos registros ficam com as empresas autorizadas.' : 'Os lembretes já chegam para você com o que o dono e as empresas registrarem.'}</p>
          <ion-button expand="block" class="empty-cta" data-action="vehicle">Ver o veículo</ion-button>
        </div>`.value;
      return;
    }
    if (s === 'intro') body.innerHTML = this.intro().value;
    if (s === 'km') body.innerHTML = this.kmStep().value;
    if (typeof s === 'number') body.innerHTML = this.question(this.questions[s]).value;
    if (s === 'exact') body.innerHTML = this.exactStep(this.questions[this.qi]).value;
    if (s === 'done') body.innerHTML = this.done().value;
    this.bindInputs();
  }

  bindInputs() {
    const km = this.$('.km-input');
    if (km) {
      km.value = this.km ? Number(this.km).toLocaleString('pt-BR') : '';
      km.addEventListener('ionInput', (e) => {
        const d = onlyDigits(e.target.value).slice(0, 7);
        e.target.value = d ? Number(d).toLocaleString('pt-BR') : '';
        this.$('.km-error').textContent = '';
      });
      km.addEventListener('keyup', (e) => e.key === 'Enter' && this.kmNext());
      setTimeout(() => km.setFocus?.(), 350);
    }
    const month = this.$('.month-input');
    if (month) {
      const a = this.answers[this.questions[this.qi].key];
      month.value = a?.answer === 'EXACT' ? a.month : '';
      this.$('.lastkm-input').value = a?.answer === 'EXACT' ? a.lastKm : '';
      month.addEventListener('ionChange', () => this.preview());
      month.addEventListener('ionInput', () => this.preview());
      this.$('.lastkm-input').addEventListener('ionInput', (e) => {
        e.target.value = onlyDigits(e.target.value).slice(0, 7);
        this.preview();
      });
      this.preview();
    }
  }

  intro() {
    const fix = Boolean(this.existing);
    return html`
      <div class="wiz-hero"><ion-icon name="notifications-outline"></ion-icon></div>
      <h1 class="wiz-title">${fix ? 'Corrigir os dados iniciais' : 'Nunca mais esqueça uma troca'}</h1>
      <p class="wiz-lead">
        ${fix
          ? `Você pode corrigir até ${fmtDay(this.existing.correctUntil)}. As respostas anteriores já vêm marcadas.`
          : `Responda ${this.total} ${this.total === 1 ? 'pergunta rápida' : 'perguntas rápidas'} sobre o ${this.label}. Calculamos as próximas revisões e avisamos por notificação e e-mail antes de vencer.`}
      </p>
      <ul class="wiz-list">
        <li><span class="wiz-ic"><ion-icon name="${hours() ? 'tc-hourmeter' : 'speedometer-outline'}"></ion-icon></span>${hours() ? 'Horímetro' : 'Km atual'}<em class="need">${hours() ? 'necessário' : 'necessária'}</em></li>
        ${this.questions.map((q) => html`<li><span class="wiz-ic"><ion-icon name="${OWNER_ICON[q.icon] ?? 'construct-outline'}"></ion-icon></span>${q.name}<em>opcional</em></li>`)}
      </ul>
      <div class="owner-note">
        <ion-icon name="person-outline"></ion-icon>
        <span>O que você informar aparece no histórico como <b>Informado por você</b>, separado dos serviços registrados por empresas.</span>
      </div>
      <div class="wiz-foot">
        <ion-button expand="block" data-action="start">${fix ? 'Corrigir' : 'Começar'}</ion-button>
        <ion-button expand="block" fill="clear" data-action="vehicle">Agora não</ion-button>
      </div>`;
  }

  kmStep() {
    return html`
      <h1 class="wiz-title">${this.kmQuestion.replaceAll('{carro}', this.label)}</h1>
      <p class="wiz-lead">${hours() ? 'Olhe no painel da máquina: é o número do horímetro (horas de uso).' : 'Olhe no painel: é o número maior do hodômetro.'}</p>
      <ion-input class="km-input" aria-label="Km atual" fill="outline" inputmode="numeric" enterkeyhint="next" placeholder="0" maxlength="9">
        <span slot="end" class="km-unit">${meterShort()}</span>
      </ion-input>
      <p class="field-error km-error" role="alert"></p>
      <div class="wiz-foot"><ion-button expand="block" data-action="km-next">Continuar</ion-button></div>`;
  }

  question(q) {
    const cur = this.answers[q.key];
    return html`
      <div class="wiz-icon"><ion-icon name="${OWNER_ICON[q.icon] ?? 'construct-outline'}"></ion-icon></div>
      <h1 class="wiz-title">${q.question}</h1>
      <p class="wiz-lead">Uma estimativa já basta.</p>
      <div class="answers" role="group" aria-label="${q.question}">
        ${OWNER_ANSWERS.map((a) => html`<button type="button" class="answer ${a.key === 'UNKNOWN' ? 'muted' : ''} ${cur?.answer === a.key ? 'on' : ''}" aria-pressed="${cur?.answer === a.key ? 'true' : 'false'}" data-action="answer" data-answer="${a.key}">${a.label}</button>`)}
      </div>
      <button type="button" class="link-btn exact-link ${cur?.answer === 'EXACT' ? 'on' : ''}" data-action="exact"><ion-icon name="calendar-outline"></ion-icon>${cur?.answer === 'EXACT' ? `Data exata: ${fmtMonth(`${cur.month}-15`)}` : 'Sei a data exata'}</button>
      <p class="wiz-hint">Toque numa opção para seguir</p>`;
  }

  exactStep(q) {
    const today = todayBR();
    return html`
      <h1 class="wiz-title">${q.name}: última troca</h1>
      <ion-input class="month-input" type="month" label="Mês e ano" label-placement="stacked" fill="outline" max="${today.slice(0, 7)}" min="1980-01"></ion-input>
      <ion-input class="lastkm-input" label="Km naquela troca (opcional)" label-placement="stacked" fill="outline" inputmode="numeric" maxlength="7" placeholder="Ex.: 52000"></ion-input>
      <p class="wiz-hint left">${(q.intervalMeter ?? q.intervalKm) ? (hours() ? 'Com o horímetro, avisamos também pelas horas de uso, não só pela data.' : 'Com a km, avisamos também pela quilometragem, não só pela data.') : 'Este item é lembrado pela data.'}</p>
      <div class="next-box" hidden>
        <span>Próxima troca</span>
        <strong></strong>
        <span></span>
      </div>
      <p class="field-error exact-error" role="alert"></p>
      <div class="wiz-foot">
        <ion-button expand="block" data-action="exact-next">Continuar</ion-button>
        <ion-button expand="block" fill="clear" data-action="quick">Voltar às opções rápidas</ion-button>
      </div>`;
  }

  done() {
    const r = this.result;
    const byKey = new Map(r.upcoming.map((u) => [u.typeKey, u]));
    const rows = r.initialData.items.map((it) => byKey.get(it.typeKey) ?? { ...it, status: 'OK' });
    const checks = rows.filter((u) => u.check);
    return html`
      <div class="wiz-done"><ion-icon name="checkmark-circle-outline"></ion-icon></div>
      <h1 class="wiz-title">${this.existing ? 'Pronto! Dados corrigidos' : 'Pronto! Lembretes ativados'}</h1>
      <p class="wiz-lead">Vamos avisar por notificação e e-mail antes de cada vencimento.</p>
      ${rows.length
        ? html`
          <div class="panel">
            ${rows.map((u) => {
              const q = this.questions.find((x) => x.key === u.typeKey);
              const st = u.check ? { color: 'warning', label: 'Verificar' } : UPCOMING_STATUS[u.status];
              return html`
                <div class="row">
                  <span class="row-icon ${u.check ? 'warning' : ''}"><ion-icon name="${OWNER_ICON[q?.icon] ?? 'construct-outline'}"></ion-icon></span>
                  <div class="row-main">
                    <span class="row-title">${u.typeName}</span>
                    <span class="row-sub">${u.check ? 'Você não lembra da última troca' : nextText(u)}${u.estimated && !u.check ? ' · estimado' : ''}</span>
                  </div>
                  ${pill(st.color, st.label)}
                </div>`;
            })}
          </div>`
        : html`<p class="wiz-lead">Km registrada: ${fmtKm(r.currentKm)}. Quando uma empresa registrar os serviços, os lembretes começam.</p>`}
      ${checks.length
        ? html`
          <div class="panel notice">
            <span class="row-icon warning"><ion-icon name="alert-circle-outline"></ion-icon></span>
            <div><p>${checks.map((u) => u.typeName).join(', ')}: peça para a empresa verificar na próxima visita. Deixamos como revisão em breve até ${fmtDay(checks[0].nextDueDate)}.</p></div>
          </div>`
        : ''}
      ${r.initialData.correctUntil ? html`<p class="wiz-hint left">Errou algo? Você pode corrigir até ${fmtDay(r.initialData.correctUntil)} ou até uma empresa registrar um serviço.</p>` : ''}
      <div class="wiz-foot">
        <ion-button expand="block" data-action="vehicle">Ver meu veículo</ion-button>
        ${r.initialData.canCorrect ? html`<ion-button expand="block" fill="clear" data-action="redo">Corrigir uma resposta</ion-button>` : ''}
      </div>`;
  }
}

customElements.define('page-initial-data', InitialDataPage);
